import React, { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

const baseControl =
  'w-full rounded border border-gray-200 bg-white px-3 py-2 text-sm font-[450] text-gray-700 outline-none transition-colors focus:border-brand-700';

const errorControl = 'border-fireside-500 focus:border-fireside-600';

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label: string;
};

const FieldWrapper: FC<FieldWrapperProps> = ({ label, error, children }) => (
  <label className="flex w-full flex-col gap-1">
    <span className="text-xs font-[450] text-gray-500">{label}</span>
    {children}
    {error && <span className="text-xs font-[450] text-fireside-600">{error}</span>}
  </label>
);

export type TextFieldProps = {
  error?: string;
  label: string;
  list?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'email' | 'text';
  value: string;
};

export const TextField: FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  type = 'text',
  list,
}) => (
  <FieldWrapper error={error} label={label}>
    <input
      className={twMerge(baseControl, error && errorControl)}
      list={list}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      type={type}
      value={value}
    />
  </FieldWrapper>
);

export type DateFieldProps = {
  error?: string;
  label: string;
  max?: string;
  min?: string;
  onChange: (value: string) => void;
  value: string;
};

export const DateField: FC<DateFieldProps> = ({ label, value, onChange, error, min, max }) => (
  <FieldWrapper error={error} label={label}>
    <input
      className={twMerge(baseControl, error && errorControl)}
      max={max}
      min={min}
      onChange={(event) => onChange(event.target.value)}
      type="date"
      value={value}
    />
  </FieldWrapper>
);

export type TextAreaFieldProps = {
  error?: string;
  label: string;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
};

export const TextAreaField: FC<TextAreaFieldProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder,
}) => (
  <FieldWrapper error={error} label={label}>
    <textarea
      className={twMerge(baseControl, 'min-h-[72px] resize-y', error && errorControl)}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      value={value}
    />
  </FieldWrapper>
);

export type SelectOptionItem = {
  label: string;
  value: string;
};

export type SelectFieldProps = {
  className?: string;
  error?: string;
  label?: string;
  onChange: (value: string) => void;
  options: SelectOptionItem[];
  value: string;
};

// Plain native select, styled to match the panel. Used for both form inputs and list filters.
export const SelectField: FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  className,
}) => {
  const control = (
    <select
      className={twMerge(baseControl, 'cursor-pointer', error && errorControl, className)}
      onChange={(event) => onChange(event.target.value)}
      value={value}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  if (!label) return control;

  return (
    <FieldWrapper error={error} label={label}>
      {control}
    </FieldWrapper>
  );
};
